import React, { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import { usePassCodeur } from '../lib/pass-codeur-context';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { CheckCircle, Crown, ArrowRight } from 'lucide-react';
import { DiscordIcon } from '../components/icons/SvgIcons';
import { EXTERNAL_LINKS } from '@/constants';
import { verifyCheckoutSession } from '../lib/stripe-service';

const PassCodeurSuccess = () => {
  const { currentUser } = useAuth();
  const { loadPassCodeurStatus } = usePassCodeur();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const hasLoadedRef = useRef(false);
  
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    // Vérifier le paiement et recharger le statut du Pass Codeur après l'achat (une seule fois)
    if (currentUser && sessionId && !hasLoadedRef.current) {
      hasLoadedRef.current = true;
      
      // Vérifier le statut de la session de paiement
      verifyCheckoutSession(sessionId)
        .then(() => {
          // Recharger le statut du Pass Codeur une fois le paiement confirmé
          loadPassCodeurStatus();
        })
        .catch((error) => {
          console.error('Erreur lors de la vérification du paiement:', error);
          // Même en cas d'erreur, on recharge le statut
          loadPassCodeurStatus();
        });
    }
  }, [currentUser, sessionId]); // Retirer loadPassCodeurStatus des dépendances

  if (!currentUser) {
    navigate('/login');
    return null;
  }

  return (
    <div className="min-h-screen ">
      <div className="container mx-auto px-6 py-16">
        <div className="max-w-4xl mx-auto">
          {/* Animation de succès - Plus grande et plus impressionnante */}
          <div className="text-center mb-16">
            <div className="flex justify-center mb-8">
              <div className="relative bg-white/20 backdrop-blur-sm rounded-full p-8 shadow-2xl">
                <CheckCircle className="h-32 w-32 text-green-500 animate-bounce" />
                <Crown className="h-16 w-16 text-yellow-500 absolute -top-4 -right-4 animate-pulse" />
              </div>
            </div>
            <h1 className="text-6xl md:text-7xl font-black bg-gradient-to-r from-yellow-300 via-orange-300 to-pink-300 bg-clip-text text-transparent mb-6">
              FÉLICITATIONS ! 🎉
            </h1>
            <p className="text-2xl md:text-3xl text-foreground font-semibold">
              Ton Pass Codeur a été activé avec succès !
            </p>
          </div>

          {/* Carte de confirmation - Plus moderne et plus grande */}
          <Card className="mb-12 bg-white/70 dark:bg-slate-800/70 backdrop-blur-sm shadow-2xl border-2 border-blue-200 dark:border-blue-800 rounded-3xl">
            <CardHeader className="text-center py-10">
              
              <CardTitle className="text-4xl font-bold bg-gradient-to-r from-amber-400 via-yellow-500 to-orange-500 bg-clip-text text-transparent">Pass Codeur Activé ✨</CardTitle>
              <CardDescription className="text-xl text-foreground mt-4">
                Tu as maintenant accès à tous les cours de programmation et fonctionnalités premium
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-8 px-10 pb-10">
              

              {sessionId && (
                <div className="text-base text-muted-foreground text-center bg-muted/30 p-4 rounded-lg">
                  🔐 ID de transaction : <span className="font-mono">{sessionId}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Actions - Plus grandes et plus attirantes */}
          <div className="flex flex-col sm:flex-row gap-6 justify-center mb-16">
            <Button 
              className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white text-2xl px-16 py-8 rounded-full font-bold shadow-xl transform hover:scale-105 transition-all"
              size="lg"
              asChild
            >
              <a href="/courses">
                Découvrir les cours
                <ArrowRight className="h-6 w-6 ml-3" />
              </a>
            </Button>
          </div>

          {/* Message de bienvenue - Plus grand et plus moderne */}
          <div className="mt-16 text-center">
            <div className="bg-muted/30 dark:bg-muted/30 p-10 rounded-3xl border border-border shadow-lg">
              <h3 className="text-3xl font-bold mb-6 text-foreground">
                🎉 Bienvenue dans la communauté Mon Premier Jeu !
              </h3>
                              <p className="text-xl text-muted-foreground leading-relaxed max-w-3xl mx-auto mb-8">
                  N'hésite pas à rejoindre le Discord pour te présenter, poser tes questions et partager tes créations !
                </p>
                
                {/* Bouton Discord */}
                <Button 
                  size="lg" 
                  className="bg-[#5865F2] text-white hover:bg-[#4752C4] text-xl px-12 py-6 rounded-full font-bold shadow-xl transform hover:scale-105 transition-all"
                  asChild
                >
                  <a href={EXTERNAL_LINKS.DISCORD} target="_blank" rel="noopener noreferrer">
                    <DiscordIcon className="mr-3 w-6 h-6" /> Rejoindre Discord
                  </a>
                </Button>
                              
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PassCodeurSuccess; 